import React, { useEffect, useState } from 'react';
import { Box, Select, Table, Thead, Tbody, Tr, Th, Td, Text, FormLabel, Button, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, Spinner, useToast } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { getSmsChannelData, getSmsGatways, sendSmsChannelData } from '../../Services/api';
import * as _ from 'lodash';
import { Kaleyra, Sinch, Telnyx, RouteMobile, Equitas, TrustSignal, SBI } from './SMSForms';
import PrimaryButton from '../../components/themeComponents/PrimaryButton';
import SecondaryButton from '../../components/themeComponents/SecondaryButton';
import { CiEdit } from 'react-icons/ci';

const ApiResponseTable = () => {
  const [data, setData] = useState([]);
  const [modalData, setModalData] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedProvider, setSelectedProvider] = useState(''); 
  
  const toast = useToast();

  const initialData = {
    TrustSignal: { apiKey: '', senderId: '', gatewayType: '', route: '', isNew: true },
    RouteMobile: { userName: '', password: '', senderId: '', gatewayType: '', dltEntityId: '', isNew: true },
    Kaleyra: { name: '', apiKey: '', senderId: '', gatewayType: '', isNew: true },
    Telnyx: { userName: '', profileid: '', phone: '', isNew: true },
    Sinch: { appid: '', pass: '', name: '', senderId: '', isNew: true },
    Equitas: { channelID: '', transactionType: '', transactionSubType: '', conversationID: '', externalReferenceId: '', isAsync: false, isNew: true },
  };

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getSmsChannelData();
      // Transform the response to include provider keys
      const transformedData = Object.entries(response?.data || {})
        .flatMap(([provider, items]) =>
          Array.isArray(items) && items.length > 0
            ? items.map(item => ({ ...item, provider }))
            : []
        );
      setData(transformedData);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch data. Please try again later.');
      toast({ title: 'Error', description: err?.message || 'An error occurred.', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openModal = (item = null) => {
    setModalData(item || {});
    setSelectedProvider(item?.provider || ''); 
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setModalData(null);
    setSelectedProvider('');
  };

  const availableProviders = {
    kaleyra: Kaleyra,
    telnyx: Telnyx,
    sinch: Sinch,
    trustsignal: TrustSignal,
    equitas: Equitas,
    routemobile: RouteMobile,
    sbi: SBI,
  };

  const registerSmsChannelData = async (data, gateway) => {
    setLoading(true);
    try {
      const response = await sendSmsChannelData(data, gateway);
      return response;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const renderTableHeaders = () => {
    return ['Name', 'Provider', 'Actions'].map((key) => <Th key={key}>{key}</Th>);
  };

  const renderTableRows = () => {
    return data?.map((item, index) => (
      <Tr key={index}>
        <Td>{String(item.name || 'N/A')}</Td>
        <Td>{_.upperCase(String(item.provider)) }</Td>
        <Td>
          <SecondaryButton onClick={() => openModal(item)} label={'Edit'} rightIcon={<CiEdit />} />
        </Td>
      </Tr>
    ));
  };

  const FormComponent = selectedProvider ? availableProviders[selectedProvider.toLowerCase()] : null;

  return (
    <Box bg={'#fff'} padding={'1rem'} >
      <Box  p={'1rem'} display={'flex'} justifyContent={'space-between'} alignItems={'center'} >
        <Text fontSize={'1vw'} >
          SMS Provider Settings
        </Text>
        <PrimaryButton label={'Add new +'} mt={4} onClick={() => openModal()} />
      </Box>

      {loading ? (
        <Spinner mt={4} />
      ) : error ? (
        <Text mt={4} color='red.500'>{error}</Text>
      ) : data?.length > 0 ? (
        <Table variant='simple' bg={'#fff'} p={'1rem'} mt={4}>
          <Thead>
            <Tr>{renderTableHeaders()}</Tr>
          </Thead>
          <Tbody>{renderTableRows()}</Tbody>
        </Table>
      ) : (
        <Text mt={4}>No data available</Text>
      )}

      <Modal isOpen={isOpen} onClose={closeModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{modalData?.isNew ? 'Add Setting' : 'Edit Setting'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box mb={4}>
              <FormLabel>Select Provider</FormLabel>
              <Select
                placeholder='Select a provider'
                value={selectedProvider}
                onChange={(e) => {
                  setSelectedProvider(e.target.value);
                  setModalData(initialData[e.target.value] || {});
                }}
              >
                {Object.keys(availableProviders).map((key) => (
                  <option key={key} value={key}>
                    {_.capitalize(key)}
                  </option>
                ))}
              </Select>
            </Box>

            {FormComponent && (
              <Formik
                initialValues={modalData}
                enableReinitialize
                onSubmit={async (values) => {
                  try {
                    await registerSmsChannelData(values, selectedProvider);
                    fetchData();
                    toast({ title: 'Success', description: 'Setting saved successfully', status: 'success' });
                    closeModal();
                  } catch (e) {
                    toast({ title: 'Oops!', description: 'Something went wrong', status: 'error' });
                  }
                }}
              >
                {({ handleSubmit }) => (
                  <Form>
                    <FormComponent />
                    <PrimaryButton 
                      mt={4} 
                      onClick={handleSubmit} 
                      label={'Submit'} 
                      isDisabled={!selectedProvider}
                      isLoading={loading}
                    />
                  </Form>
                )}
              </Formik>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export const SmsSettingList = () => <ApiResponseTable />;